// Timezone Helper Utilities
// Converts dates between UTC storage and user's local timezone

/**
 * Get today's date in user's timezone (YYYY-MM-DD format)
 *
 * @param userTimezone - IANA timezone string (e.g., "America/New_York")
 * @returns Date string in YYYY-MM-DD format for user's current day
 *
 * @example
 * // User in EST, it's 8:00 PM on Oct 5
 * // UTC time is already Oct 6 at 12:00 AM
 * getUserToday("America/New_York") // Returns "2025-10-05"
 *
 * // Without timezone awareness:
 * new Date().toISOString().split('T')[0] // Returns "2025-10-06" (WRONG!)
 */
export function getUserToday(userTimezone: string): string {
  const now = new Date();

  // Convert to user's timezone
  const userDate = new Date(now.toLocaleString('en-US', {
    timeZone: userTimezone
  }));

  // Format as YYYY-MM-DD
  const year = userDate.getFullYear();
  const month = String(userDate.getMonth() + 1).padStart(2, '0');
  const day = String(userDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Convert a date string to user's timezone for display
 *
 * @param dateString - Date in YYYY-MM-DD format (stored in UTC)
 * @param userTimezone - IANA timezone string
 * @returns Formatted date string in user's timezone
 */
export function formatDateForUser(dateString: string, userTimezone: string): string {
  const date = new Date(dateString + 'T00:00:00Z'); // Treat as UTC midnight

  return date.toLocaleDateString('en-US', {
    timeZone: userTimezone,
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * Get a date offset by N days from today in user's timezone
 *
 * @param userTimezone - IANA timezone string
 * @param daysOffset - Number of days to offset (negative for past, positive for future)
 * @returns Date string in YYYY-MM-DD format
 *
 * @example
 * getUserDateOffset("America/New_York", -1) // Yesterday in EST
 * getUserDateOffset("America/New_York", 0)  // Today in EST (same as getUserToday)
 * getUserDateOffset("America/New_York", 1)  // Tomorrow in EST
 */
export function getUserDateOffset(userTimezone: string, daysOffset: number): string {
  const now = new Date();

  // Convert to user's timezone
  const userDate = new Date(now.toLocaleString('en-US', {
    timeZone: userTimezone
  }));

  // Apply offset
  userDate.setDate(userDate.getDate() + daysOffset);

  // Format as YYYY-MM-DD
  const year = userDate.getFullYear();
  const month = String(userDate.getMonth() + 1).padStart(2, '0');
  const day = String(userDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

/**
 * Parse a date string and get day of week in user's timezone
 *
 * @param dateString - Date in YYYY-MM-DD format
 * @param userTimezone - IANA timezone string
 * @returns Day of week name (e.g., "Monday")
 */
export function getDayOfWeek(dateString: string, userTimezone: string): string {
  const date = new Date(dateString + 'T00:00:00Z');

  return date.toLocaleDateString('en-US', {
    timeZone: userTimezone,
    weekday: 'long'
  });
}

/**
 * Check if a date string represents today in user's timezone
 *
 * @param dateString - Date in YYYY-MM-DD format
 * @param userTimezone - IANA timezone string
 * @returns true if date is today in user's timezone
 */
export function isToday(dateString: string, userTimezone: string): boolean {
  return dateString === getUserToday(userTimezone);
}

/**
 * Get start and end dates for a date range in user's timezone
 *
 * @param range - Range type ('7days', '30days', 'month', 'year')
 * @param userTimezone - IANA timezone string
 * @returns Object with startDate and endDate in YYYY-MM-DD format
 */
export function getDateRange(
  range: '7days' | '30days' | 'month' | 'year',
  userTimezone: string
): { startDate: string; endDate: string } {
  const endDate = getUserToday(userTimezone);

  let daysBack: number;
  switch (range) {
    case '7days':
      daysBack = 6; // Today + 6 days back = 7 days total
      break;
    case '30days':
      daysBack = 29;
      break;
    case 'month':
      daysBack = 30;
      break;
    case 'year':
      daysBack = 364;
      break;
    default:
      daysBack = 6;
  }

  const startDate = getUserDateOffset(userTimezone, -daysBack);

  return { startDate, endDate };
}
